import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { X, Upload, Download, FileSpreadsheet, FileText, Loader2, Star, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { parseKeywordsFromText, parseKeywordsFromFile, downloadKeywordTemplateCsv } from "@/utils/keywordInput";
import { filterValidKeywords } from "@/utils/keywordValidation";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

type HeroProductOption = { asin: string; name: string };

interface AddKeywordsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddKeywords: (keywords: string[], starredKeywords: string[]) => void | Promise<void>;
  isSubmitting?: boolean;
  heroAsin?: string;
  onHeroAsinChange?: (asin: string) => void;
  heroProducts?: HeroProductOption[];
  maxKeywords?: number;
  showMasterListOption?: boolean;
}

export function AddKeywordsModal({
  open,
  onOpenChange,
  onAddKeywords,
  isSubmitting = false,
  heroAsin,
  onHeroAsinChange,
  heroProducts = [],
  maxKeywords,
  showMasterListOption = false,
}: AddKeywordsModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [parsedKeywords, setParsedKeywords] = useState<string[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [activeTab, setActiveTab] = useState("manual");
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [starred, setStarred] = useState<Set<string>>(new Set());
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requiresHeroSelection = heroProducts.length > 0 && !!onHeroAsinChange;
  const isOverLimit = maxKeywords !== undefined && parsedKeywords.length > maxKeywords;

  const resetState = () => {
    setInputValue("");
    setParsedKeywords([]);
    setSkippedCount(0);
    setUploadedFileName(null);
    setActiveTab("manual");
    setStarred(new Set());
    setFileError(null);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const rawSplit = value.split(/[\n,]+/).map((kw) => kw.trim().toLowerCase());
    const { tooShort } = filterValidKeywords(rawSplit);
    setSkippedCount(tooShort.length);
    const next = parseKeywordsFromText(value);
    setParsedKeywords(next);
    setStarred((prev) => {
      const nextSet = new Set<string>();
      for (const kw of prev) {
        if (next.includes(kw)) nextSet.add(kw);
      }
      return nextSet;
    });
  };

  const toggleStar = (kw: string) => {
    setStarred((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    const remaining = parsedKeywords.filter((kw) => kw !== keywordToRemove);
    setParsedKeywords(remaining);
    setInputValue(remaining.join("\n"));
    setStarred((prev) => { const next = new Set(prev); next.delete(keywordToRemove); return next; });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileError(null);
    setUploadedFileName(file.name);
    setStarred(new Set());
    try {
      const { keywords, skippedCount } = await parseKeywordsFromFile(file);
      if (keywords.length === 0) {
        toast({
          title: "No keywords found",
          description: "No valid keywords found in the uploaded file. Make sure keywords are listed in Column A.",
          variant: "destructive",
        });
        setParsedKeywords([]);
        setInputValue("");
        setSkippedCount(0);
      } else {
        setParsedKeywords(keywords);
        setInputValue(keywords.join("\n"));
        setSkippedCount(skippedCount);
      }
    } catch {
      toast({
        title: "File parsing failed",
        description: "Failed to parse the uploaded file. Please check the file format and try again.",
        variant: "destructive",
      });
      setParsedKeywords([]);
      setInputValue("");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (parsedKeywords.length === 0 || isSubmitting || isOverLimit) return;
    if (requiresHeroSelection && !heroAsin) return;
    try {
      await onAddKeywords(parsedKeywords, Array.from(starred));
      resetState();
    } catch (_err) { void _err; }
  };

  const keywordCountLabel = maxKeywords !== undefined
    ? `${parsedKeywords.length} / ${maxKeywords} keyword${parsedKeywords.length !== 1 ? "s" : ""} selected`
    : `${parsedKeywords.length} keyword${parsedKeywords.length !== 1 ? "s" : ""} selected`;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !isSubmitting) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-2xl bg-card">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Add Keywords</DialogTitle>
          <DialogDescription>
            {showMasterListOption
              ? "Add keywords to your Keyword Bank. Star any that should also be added to your Master Keyword List."
              : "Add keywords manually or upload a file. Keywords will be added to rank tracking."}
          </DialogDescription>
        </DialogHeader>

        {requiresHeroSelection && (
          <div className="space-y-2">
            <Label htmlFor="hero-asin-select">Select Hero ASIN</Label>
            <Select value={heroAsin} onValueChange={onHeroAsinChange}>
              <SelectTrigger id="hero-asin-select">
                <SelectValue placeholder="Choose a product to track keywords for..." />
              </SelectTrigger>
              <SelectContent>
                {heroProducts.map((product) => (
                  <SelectItem key={product.asin} value={product.asin} className="pl-2">
                    {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- inside-an-open-overlay */}
                    <span className="block truncate max-w-[26rem]">{product.asin} - {product.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList variant="underline">
            <TabsTrigger variant="underline" value="manual" className="gap-2">
              <FileText className="h-4 w-4" />
              Manual Entry
            </TabsTrigger>
            <TabsTrigger variant="underline" value="upload" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              File Upload
            </TabsTrigger>
          </TabsList>

          <div className="min-h-[420px] pt-2">
            <TabsContent value="manual" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="keywords">Keywords</Label>
                <Textarea
                  id="keywords"
                  placeholder={`Enter keywords, one per line or comma-separated${maxKeywords !== undefined ? ` (up to ${maxKeywords})` : ""}\n\nbeach bag\nwaterproof tote\nsummer accessories`}
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="min-h-[150px]"
                />
                {showMasterListOption && (
                  <p className="text-xs text-muted-foreground">
                    Tip: star keywords in the selected list below to also add them to your Master List.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="upload" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" id="keyword-file-upload" />
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">Upload a CSV or Excel file with keywords in Column A</p>
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4" />
                      Choose File
                    </Button>
                    <Button variant="ghost" size="sm" onClick={downloadKeywordTemplateCsv}>
                      <Download className="h-4 w-4" />
                      Download Template
                    </Button>
                  </div>
                  {uploadedFileName && <p className="text-sm text-primary mt-2">Uploaded: {uploadedFileName}</p>}
                  {fileError && (
                    <div className="flex items-start gap-2 mt-2 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{fileError}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Supported formats: CSV, XLSX, XLS. Keywords should be listed in Column A.</p>
                  {maxKeywords !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      You can add up to {maxKeywords.toLocaleString()} keywords at a time.
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {(parsedKeywords.length > 0 || skippedCount > 0) && (
          <div className="space-y-2 pt-2 border-t">
            {parsedKeywords.length > 0 && (
              <Label className={`text-sm ${isOverLimit ? "text-destructive" : "text-muted-foreground"}`}>
                {keywordCountLabel}
                {showMasterListOption && starred.size > 0 ? ` · ${starred.size} starred for Master List` : ""}
              </Label>
            )}
            {skippedCount > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {skippedCount} keyword{skippedCount !== 1 ? "s" : ""} skipped — must be at least 2 characters
              </p>
            )}
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
              {parsedKeywords.slice(0, 50).map((keyword) => {
                const isStarred = starred.has(keyword);
                return (
                  <Badge key={keyword} variant="secondary" className="text-xs pr-1 pl-2 gap-1">
                    {showMasterListOption && (
                      <button
                        type="button"
                        onClick={() => toggleStar(keyword)}
                        aria-label={isStarred ? "Unstar" : "Star to add to Master List"}
                        className="rounded-full p-0.5 hover:bg-muted"
                      >
                        <Star className={cn("h-3 w-3", isStarred ? "fill-amber-400 text-amber-500" : "text-muted-foreground")} />
                      </button>
                    )}
                    <span>{keyword}</span>
                    <button onClick={() => handleRemoveKeyword(keyword)} className="ml-1 hover:bg-muted rounded-full p-0.5" aria-label={`Remove ${keyword}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
              {parsedKeywords.length > 50 && (
                <span className="text-xs text-muted-foreground self-center">+{parsedKeywords.length - 50} more</span>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { if (!isSubmitting) onOpenChange(false); }} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={parsedKeywords.length === 0 || isSubmitting || isOverLimit || (requiresHeroSelection && !heroAsin)}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add {parsedKeywords.length > 0 ? parsedKeywords.length : ""} Keyword{parsedKeywords.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
